import cn from 'classnames';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../UI/Button/Button';
import { LikeIcon, RightArrowIcon } from '../../assets/icons';
import photoSrc from '../../assets/images/EventImage.png';
import { useAppSelector } from '../../hooks/reduxHooks/useAppSelector';
import { useChangeLike } from '../../hooks/useChangeLike';
import { IEventCard, Role } from '../../types/types';
import { DeleteModal } from '../DeleteModal/DeleteModal';
import { LocationMark } from '../LocationMark/LocationMark';
import { Tag } from '../Tag/Tag';
import styles from './EventCard.module.scss';

export interface EventCardProps {
    event: IEventCard;
    containerClassName?: string;
    titleClassName?: string;
    imageClassName?: string;
    smallBlocksClassName?: string;
}

export const EventCard = ({
    event,
    containerClassName,
    titleClassName,
    imageClassName,
    smallBlocksClassName,
}: EventCardProps) => {
    const navigate = useNavigate();
    const [isCardLiked, setIsCardLiked] = useState(
        event.in_user_favorite_events,
    );

    const { isLogin, role } = useAppSelector((state) => state.authReducer);

    const { likeEvent, cancelLikeEvent } = useChangeLike(event.id);

    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const eventUrl = `/v1/event/${event.id}/`;

    // в архиве событий среди мероприятий идут и новости с галочкой
    // «Это мероприятие»: ведут на новость, без избранного, формат и место
    // у них необязательны, правятся из раздела новостей
    const isNews = event.kind === 'news';
    const cardLink = isNews ? `/news/${event.id}` : `/events/${event.id}`;

    return (
        <div className={styles.eventcard}>
            <Link
                to={cardLink}
                className={cn(styles.eventcard__imagecontainer, imageClassName)}
            >
                <img
                    className={styles.eventcard__image}
                    src={event.image ? event.image : photoSrc}
                    alt="событие"
                />
            </Link>
            <div className={cn(styles.eventcard__wrapper, containerClassName)}>
                <div className={styles.eventcard__top}>
                    {event.format ? (
                        <Tag
                            type="format"
                            content={event.format === 'ON' ? 'Онлайн' : 'Офлайн'}
                        />
                    ) : null}
                    <Tag
                        type="date"
                        content={new Date(event.datetime).toLocaleDateString(
                            'ru-RU',
                            // даты мероприятий московские: без пояса браузер
                            // западнее Москвы показывает предыдущий день
                            { timeZone: 'Europe/Moscow' },
                        )}
                    />
                    {role !== Role.staff && !isNews ? (
                        <LikeIcon
                            onClick={() => {
                                if (!isLogin) {
                                    return navigate({
                                        search: '?login=true',
                                    });
                                }
                                isCardLiked ? cancelLikeEvent() : likeEvent();
                                setIsCardLiked(!isCardLiked);
                            }}
                            className={
                                isCardLiked
                                    ? styles.eventcard__like_active
                                    : styles.eventcard__like
                            }
                        />
                    ) : null}
                </div>
                <Link
                    to={cardLink}
                    className={styles.eventcard__middle}
                >
                    <h3
                        className={cn(styles.eventcard__header, titleClassName)}
                    >
                        {event.title}
                    </h3>
                </Link>
                <div className={styles.eventcard__bottom}>
                    <Button className={styles.eventcard__moreinfo}>
                        <Link
                            onClick={() => {
                                window.scroll({
                                    top: 0,
                                    behavior: 'smooth',
                                });
                            }}
                            className={cn(
                                styles.eventcard__moreinfo_link,
                                smallBlocksClassName,
                            )}
                            to={cardLink}
                        >
                            <span className={styles.eventcard__moreinfo_text}>
                                Подробнее
                            </span>
                            <RightArrowIcon />
                        </Link>
                    </Button>
                    {event.address ? (
                        <LocationMark text={event.address} />
                    ) : null}
                </div>
                {role === 'staff' && !isNews ? (
                    <div className={styles.buttons}>
                        <Button
                            onClick={() => {
                                navigate(`/staff/events_edit/${event.id}/`);
                            }}
                        >
                            Редактировать
                        </Button>
                        <Button
                            onClick={() => {
                                setShowDeleteModal(true);
                            }}
                        >
                            Удалить
                        </Button>
                    </div>
                ) : null}
            </div>
            {showDeleteModal ? (
                <DeleteModal
                    backLink="/staff/events"
                    url={eventUrl}
                    title="Вы действительно хотите удалить мероприятие?"
                    successMessage="Мероприятие удалено!"
                    errorMessage="Мероприятие не удалено!"
                    onClose={() => setShowDeleteModal(false)}
                />
            ) : null}
        </div>
    );
};
